import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { AlertMessage } from "@/components/ui/alert-message";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useClientCostCategories } from "@/lib/hooks/use-client-cost-categories";
import {
  useDeletePortfolioCost,
  useProductCostClasses,
} from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { AdvisorOverrideBadge } from "./advisor-override-badge";
import { ManualFeeDialog } from "./manual-fee-dialog";
import { T } from "./manual-fees-table.i18n";

type ManualFeesTableProps = {
  portfolioCollectionId: string;
  manualFees: any[];
};

export function ManualFeesTable({ portfolioCollectionId, manualFees }: ManualFeesTableProps) {
  const localize = useLocalize();
  const { formatNumber } = useRegionalFormat();
  const t = useT(T);

  const deletePortfolioCost = useDeletePortfolioCost();
  const isPending = deletePortfolioCost.isPending;

  const { clientCostCategories } = useClientCostCategories();
  const { productCostClasses } = useProductCostClasses(portfolioCollectionId);
  const clientCatName = (id: string | null) =>
    id ? localize(clientCostCategories.find((c) => c.id === id)?.nameI18n ?? {}) || t.any : t.any;
  const productClassName = (id: string | null) =>
    id ? localize(productCostClasses.find((c) => c.id === id)?.nameI18n ?? {}) || t.any : t.any;

  const CATEGORY_LABELS: Record<string, string> = {
    RECURRING: t.categoryRecurring,
    DEPOSIT: t.categoryDeposit,
    WITHDRAWAL: t.categoryWithdrawal,
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFee, setEditingFee] = useState<any | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [feeToDelete, setFeeToDelete] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  const handleEdit = (fee: any) => {
    setEditingFee(fee);
    setDialogOpen(true);
  };

  const handleDelete = () => {
    if (!feeToDelete) return;
    deletePortfolioCost.mutate(
      { collectionId: portfolioCollectionId, pcId: feeToDelete },
      {
        onSuccess: () => toast.success(t.manualFeeDeleted),
        onError: (err) => showAlert(t.error, err.message || t.failedToDelete, "destructive"),
      },
    );
    setFeeToDelete(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingFee(null);
  };

  const formatAmount = (value: number): string => formatNumber(value);

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">{t.manualFees}</h2>
            <p className="text-sm text-muted-foreground">{t.description}</p>
          </div>
          <Button onClick={() => setDialogOpen(true)}>{t.addManualFee}</Button>
        </div>

        {manualFees.length === 0 ? (
          <EmptyState>{t.noManualFees}</EmptyState>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.category}</TableHead>
                <TableHead>{t.rate}</TableHead>
                <TableHead>{t.minFee}</TableHead>
                <TableHead>{t.scope}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {manualFees.map((fee) => (
                <TableRow key={fee.id}>
                  <TableCell className="font-medium">
                    {localize(fee.nameI18n)}
                    <AdvisorOverrideBadge mode={fee.advisorOverride} />
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {CATEGORY_LABELS[fee.category] || fee.category}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {fee.valuePct != null ? `${fee.valuePct}%` : t.none}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {fee.minFeeAmount != null ? formatAmount(Number(fee.minFeeAmount)) : t.none}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {clientCatName(fee.clientCostCategoryId)} ·{" "}
                    {productClassName(fee.productCostClassId)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="icon"
                        tooltip={t.edit}
                        variant="ghost"
                        onClick={() => handleEdit(fee)}
                        disabled={isPending}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        tooltip={t.delete}
                        variant="ghost"
                        onClick={() => {
                          setFeeToDelete(fee.id);
                          setConfirmDeleteOpen(true);
                        }}
                        disabled={isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <ManualFeeDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        portfolioCollectionId={portfolioCollectionId}
        manualFee={editingFee}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteManualFee}
        description={t.deleteConfirmation}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
